import Service from './Service';
import type TokenizedApi from '../TokenizedApi';
import * as schema from '../utils/schema';

const PROTOCOL_RESOURCES_URL = 'https://liquidnoble.com/external/protocol/protocol_resources.json';

const staticResourceSchema = <TContent>(
  contentSchema:
    | schema.SchemaValueRecord
    | schema.ContainerSchemaValue<TContent>,
) =>
  schema.dictionary(
    schema.object<{
      name?: string;
      label?: string;
      description?: string;
      metadata?: TContent;
    }>({
      Name: schema.string().as('name'),
      Label: schema.string().as('label'),
      Description: schema.string().as('description'),
      MetaData: schema
        .embeddedJSON(schema.object<TContent>(contentSchema))
        .as('metadata'),
    }),
  );

export interface TCurrencyResource {
  fractionalUnit?: string;
  monetaryAuthority?: string;
  polity?: string[];
  precision?: number;
  symbol?: string;
}

export interface TPolityResource {
  flag?: string;
  phoneCode?: string;
  states?: Map<string, string>;
}

export interface TBankingPolityResource {
  states?: Map<string, string>;
}

export interface TStaticResources {
  currencies?: Map<
    string,
    {
      name?: string;
      label?: string;
      description?: string;
      metadata?: TCurrencyResource;
    }
  >;
  polityTypes?: Map<
    string,
    {
      name?: string;
      label?: string;
      description?: string;
      metadata?: TPolityResource;
    }
  >;
  bankingPolityTypes?: Map<
    string,
    {
      name?: string;
      label?: string;
      description?: string;
      metadata?: TBankingPolityResource;
    }
  >;
  entityTypes?: unknown;
  rejectionCodes?: unknown;
  roleTypes?: unknown;
  tagTypes?: unknown;
}

/** @category SDK Services */
class StaticResources extends Service {
  /** @internal */
  constructor(tokenizedApi: TokenizedApi) {
    super(tokenizedApi);
  }

  resourcesQuery = this.makeQuery(
    {
      // We want to load static data once on startup and never refetch
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: true,
    },
    async () => {
      // Query PROTOCOL_RESOURCES_URL using fetch
      const query = await fetch(PROTOCOL_RESOURCES_URL);
      const response = await query.json();
      const result = schema.normalizeResponse(
        'Query static resources',
        response?.data,
        schema.object<TStaticResources>({
          rejection_codes: staticResourceSchema({}).as('rejectionCodes'),
          currencies: staticResourceSchema({
            fractional_unit: schema.string().as('fractionalUnit'),
            monetary_authority: schema.string().as('monetaryAuthority'),
            polity: schema.arrayOf(schema.string()),
            precision: schema.number(),
            symbol: schema.string(),
          }),
          entity_types: staticResourceSchema({}).as('entityTypes'),
          polity_types: staticResourceSchema({
            flag: schema.string(),
            phone_code: schema.string().as('phoneCode'),
            states: schema.dictionary(schema.string()).as('states'),
          }).as('polityTypes'),
          banking_polity_types: staticResourceSchema({
            states: schema.dictionary(schema.string()).as('states'),
          }).as('bankingPolityTypes'),
          role_types: staticResourceSchema({}).as('roleTypes'),
          tag_types: staticResourceSchema({}).as('tagTypes'),
        }),
      ).normalizedValue;

      return result;
    },
  );
}

export default StaticResources;
